import React, { useCallback, useContext, useMemo } from 'react';
import { CButton, CCol, CForm, CRow } from '@coreui/react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import CFormGroup from '../../../../../../migration/CFormGroup';
import CLabel from '../../../../../../migration/CLabel';
import { CInput, CInputCheckbox } from '../../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../../migration/CInvalidFeedback';

import { callTokenApi } from '../../../../../../../apiCaller';
import { API_AI_INSIGHTS_INTENT_DATASET, API_AI_INSIGHTS_INTENT_DATASET_BY_ID } from '../../../../../../../constants';
import { useSelector } from 'react-redux';
import Waiting from '../../../../../../general/Loadings/Waiting';
import { AIIntentsDetailContext } from '../DateSet';
import StepCardHeader from '../../../../analytics/ai-insights/Components/StepCardHeaders';

const validationSchema = Yup.object().shape({
     name: Yup.string().trim().required('Name is required').max(200, 'Name cannot be more than 200 characters'),
});

const FormGroup = ({ name, label, errors, values, touched, disabled, ...props }) => {
     return (
          <CFormGroup className="mb-3">
               <CLabel htmlFor={name}>{label}</CLabel>
               <CInput id={name} invalid={touched[name] && !!errors[name]} value={values[name] ?? ''} {...props} disabled={disabled || false} />
               <CInvalidFeedback>{touched[name] && errors[name]}</CInvalidFeedback>
          </CFormGroup>
     );
};

const NameDescription = () => {
     const { activeStep, setActiveStep, stepsData, setStepsData, setViewDatasets, handleHasChange } = useContext(AIIntentsDetailContext);
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);

     const initialValues = useMemo(() => {
          const base = {
               name: stepsData.name || '',
               description: stepsData.description || '',
               enableDescription: stepsData.enableDescription || false,
          };
          return { ...base };
     }, [stepsData.name, stepsData.description, activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

     const onSubmit = useCallback(
          async (values) => {
               const trimmedName = values.name.trim();
               const trimmedDescription = values.description?.trim() || '';
               const enableDescription = values.enableDescription || false;

               const hasNoChanges =
                    trimmedName === stepsData.name &&
                    trimmedDescription === stepsData.description &&
                    enableDescription === stepsData.enableDescription;

               if (hasNoChanges) {
                    setActiveStep(2);
                    return;
               }

               const currentStep = 1;

               const payload = {
                    name: trimmedName,
                    oldName: stepsData.name,
                    description: trimmedDescription,
                    enableDescription,
                    accountId: activeAccount.accountId,
                    lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > currentStep ? stepsData.lastEditStep : currentStep,
               };

               try {
                    let response;
                    const isUpdate = !!stepsData?.id;

                    if (isUpdate) {
                         response = await callTokenApi(`${API_AI_INSIGHTS_INTENT_DATASET_BY_ID.replace(':id', stepsData.id)}`, 'PUT', payload);
                    } else {
                         response = await callTokenApi(API_AI_INSIGHTS_INTENT_DATASET, 'POST', payload);
                    }

                    const isSuccess = response?.data?.code === 200 || response?.data?.code === 201;
                    const responseData = response?.data?.data;

                    if (isSuccess && responseData) {
                         setStepsData({
                              ...stepsData,
                              ...payload,
                              status: responseData.status || stepsData.status,
                              id: responseData.id || stepsData.id,
                         });

                         setViewDatasets((prev) => {
                              const existsIndex = prev.findIndex((tpl) => tpl.id === responseData.id);

                              if (existsIndex !== -1) {
                                   const updatedDataSets = [...prev];
                                   updatedDataSets[existsIndex] = { ...updatedDataSets[existsIndex], ...responseData };
                                   return updatedDataSets;
                              }

                              return [responseData, ...prev];
                         });

                         toast.success(`data set ${stepsData?.id ? 'updated' : 'created'} successfully.`);
                         handleHasChange(false);
                         setActiveStep(2);
                    } else {
                         if (response?.data?.code === 409) {
                              toast.error(response.data.message || 'Intent dataset name already exists for this account');
                              return;
                         }
                         toast.error('Failed to save the data set. Please try again.');
                    }
               } catch (err) {
                    console.error('Submit error:', err);
                    toast.error('Something went wrong. Please try again later.');
               }
          },
          [setViewDatasets, setActiveStep, setStepsData, stepsData, activeAccount.accountId, handleHasChange]
     ); // eslint-disable-line react-hooks/exhaustive-deps

     return (
          <StepCardHeader
               title={`Name & Description`}
               step={1}
               activeStep={activeStep}
               lastEditStep={stepsData.lastEditStep}
               setActiveStep={() => setActiveStep(1)}
          >
               <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} validateOnBlur={true} enableReinitialize>
                    {({ values, errors, touched, handleChange, isSubmitting, handleSubmit }) => {
                         const handleChangeWithPopup = (e) => {
                              handleChange(e);
                              handleHasChange(true);
                         };
                         return (
                              <CForm onSubmit={handleSubmit} noValidate>
                                   <CRow className="mb-3">
                                        <CCol lg="12">
                                             <FormGroup
                                                  name="name"
                                                  label="Name this Classification data set."
                                                  values={values}
                                                  errors={errors}
                                                  touched={touched}
                                                  placeholder="Enter a name"
                                                  onChange={handleChangeWithPopup}
                                                  disabled={stepsData.isDefault}
                                             />
                                        </CCol>

                                        <CCol lg="12">
                                             <FormGroup
                                                  name="description"
                                                  label="Include an optional description for your own purpose. This does not affect the intent analysis."
                                                  values={values}
                                                  errors={errors}
                                                  touched={touched}
                                                  placeholder="Enter a description for your data set"
                                                  onChange={handleChangeWithPopup}
                                                  disabled={stepsData.isDefault}
                                             />
                                        </CCol>
                                        <CCol lg="12">
                                             <CFormGroup variant="custom-checkbox" inline>
                                                  <CInputCheckbox
                                                       custom
                                                       id="enableDescription"
                                                       name="enableDescription"
                                                       checked={values.enableDescription}
                                                       values={values.enableDescription}
                                                       onChange={handleChangeWithPopup}
                                                       className="m-auto"
                                                  />
                                                  <CLabel htmlFor="enableDescription" variant="custom-checkbox">
                                                       Enable a description field alongside each data value. This can help an AI model to improve the
                                                       classification but is oftentimes not required.
                                                  </CLabel>
                                             </CFormGroup>
                                        </CCol>
                                   </CRow>

                                   <CButton disabled={isSubmitting} type="submit" className="px-4" color="primary">
                                        <Waiting isLoading={isSubmitting}>Save & Next</Waiting>
                                   </CButton>
                              </CForm>
                         );
                    }}
               </Formik>
          </StepCardHeader>
     );
};

export default NameDescription;
